import {Component, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {MatDialogRef, MatDialog} from '@angular/material/dialog';
import {DialogService} from 'src/app/shared/services/dialog.service';
import {loadBase64ImageAsFile} from 'src/app/shared/helpers/helpers';
import {CroppedImageData, PhotoCropComponent} from '../photo-crop/photo-crop.component';
import {DialogConfirmComponent} from '../dialogs/dialog-confirm/dialog-confirm.component';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';

@Component({
    selector: 'app-image-upload-crop',
    templateUrl: './image-upload-crop.component.html',
    styleUrls: ['./image-upload-crop.component.scss'],
    standalone: false
})
export class ImageUploadCropComponent implements OnInit {
  @Input() formGroup: FormGroup;
  @Input() cropConf: {
    cropperMinWidth: number;
    cropperMinHeight: number;
    cropperMaxWidth: number;
    cropperMaxHeight: number;
    ratio?: string; // do not pass - calculated from max width height properties
    freeMaxCrop?: boolean; //if true allows crop area extend fully but keeping ratio
  };
  @Input() allowedExtensions: string[] = [
    'png',
    'jpg',
    'jpeg',
    'gif',
    'bmp',
    'svg',
    'webp',
    'mp4',
    'webm',
    'mov',
    'avi',
  ];
  @Input() noRemoveBtn = false;
  @Input() hideDeleteIcon = false;
  @Input() hideCropIcon = false;
  @Input() previewAsBgContain = false;
  @Input() placeholderImage = '';
  @Input() emptyDescriptionHtml;
  @Input() useOriginalForCrop = false;

  @Output() cropped = new EventEmitter<CroppedImageData>();
  @Output() imageRemoved = new EventEmitter<boolean>();

  @ViewChild('fileInput') fileInputRef: ElementRef<HTMLInputElement>;

  private _originalFile: File | null = null;
  private _originalBase64: string | null = null;

  private imgMedia: {width: number; height: number};
  private photoCropRef: MatDialogRef<PhotoCropComponent>;
  public acceptTypes!: string;

  private imageExts = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'webp'];
  private videoExts = ['mp4', 'webm', 'mov', 'avi'];
  public videoValidResolution = false;
  public safeVideoUrl: SafeUrl | null = null;
  private rawVideoUrl: string | null = null;

  mime: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    bmp: 'image/bmp',
    webp: 'image/webp',
    svg: 'image/svg+xml',
  };

  constructor(
    private sanitizer: DomSanitizer,
    private dialog: MatDialog,
    private dialogService: DialogService,
  ) {}

  ngOnInit() {
    if (this.cropConf.cropperMaxWidth && this.cropConf.cropperMaxHeight) {
      const ratio = String(this.cropConf.cropperMaxHeight / this.cropConf.cropperMaxWidth);
      this.cropConf.ratio = `1/${ratio}`;
    }
    if (this.cropConf?.freeMaxCrop) {
      this.cropConf.cropperMaxWidth = 100000;
      this.cropConf.cropperMaxHeight = 100000;
    }

    this.acceptTypes = this.allowedExtensions.map(ext => this.mime[ext] ?? `.${ext}`).join(',');
  }

  resetInput(input?: HTMLInputElement) {
    (input || this.fileInputRef.nativeElement).value = '';
  }

  onSelectFile(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!this.allowedExtensions.includes(ext)) {
      this.showWrongFormat(input);
      return;
    }

    const kind = file.type.split('/')[0];
    if (kind === 'video' && this.videoExts.includes(ext)) {
      this.handleVideo(file, input);
    } else if (kind === 'image' && this.imageExts.includes(ext)) {
      this.handleImage(file, input);
    } else {
      this.showWrongFormat(input);
    }
  }

  private handleImage(file: File, input) {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e: any) => {
      const base64 = e.target.result as string;
      const img = new Image();
      img.src = e.target.result;
      img.onload = () => {
        this.imgMedia = {width: img.width, height: img.height};
        this._originalFile = file;
        this._originalBase64 = base64;
        this.cropThisPhoto(file);
        this.resetInput(input);
      };
    };
  }

  private handleVideo(file: File, input?: HTMLInputElement) {
    this.rawVideoUrl = URL.createObjectURL(file);

    this.safeVideoUrl = this.sanitizer.bypassSecurityTrustUrl(this.rawVideoUrl);

    this.formGroup.get('source')!.setValue(this.rawVideoUrl);
    this.formGroup.get('file')!.setValue(file);
    this.formGroup.get('base')!.setValue(null);

    const vid = document.createElement('video');

    vid.src = this.rawVideoUrl;

    vid.onloadedmetadata = () => {
      const w = vid.videoWidth;
      const h = vid.videoHeight;

      this.videoValidResolution = w === this.cropConf.cropperMinWidth && h === this.cropConf.cropperMinHeight;

      if (!this.videoValidResolution) {
        this.dialogService.openDialog(DialogConfirmComponent, {
          width: '670px',
          data: {
            title: 'Wrong video dimensions',
            descriptionHtml:
              `Your video is ${w}×${h}.` +
              ` It must be exactly ` +
              `${this.cropConf.cropperMinWidth}×${this.cropConf.cropperMinHeight}.`,
            confirmBtn: 'OK',
          },
        });
        this.resetInput(input);
        this.formGroup.get('file').setValue(null);
        this.formGroup.get('base').setValue(null);
        this.formGroup.get('source').setValue('');
        this.rawVideoUrl = this.safeVideoUrl = null;
        URL.revokeObjectURL(this.rawVideoUrl!);
        return;
      }

      this.cropped.emit({
        originalImageFile: file,
        croppedImageFile: file,
        croppedImage: null,
        croppedImageResolution: {width: w, height: h},
        cropper: {x1: 0, y1: 0, x2: w, y2: h},
        isVideo: true,
      });

      this.resetInput(input);
    };
  }

  private showWrongFormat(input?: HTMLInputElement) {
    const imgs = this.allowedExtensions.filter(ext => this.imageExts.includes(ext));
    const vids = this.allowedExtensions.filter(ext => this.videoExts.includes(ext));

    this.dialogService.openDialog(DialogConfirmComponent, {
      width: '670px',
      data: {
        title: 'Wrong file format',
        descriptionHtml: `Only files of type: ${this.allowedExtensions.join(', ')} are allowed.`,
        confirmBtn: 'Close',
      },
      panelClass: ['cdk-mobile-full-screen', 'dialog-confirm-container-mobile-full-screen'],
    });

    if (input) this.resetInput(input);
  }

  cropThisPhoto(uploadedFile?: File) {
    const control = this.formGroup;
    const currentSrc = control.get('source').value as string;
    const currentBase = control.get('base').value as string | null;

    if (uploadedFile) {
      return this._openCrop(uploadedFile, true);
    }

    if (this.useOriginalForCrop && this._originalFile) {
      return this._openCrop(this._originalFile, true);
    }

    if (this.useOriginalForCrop && this._originalBase64) {
      return this._openCrop(this._originalBase64, false);
    }

    let fileOrBase: File | string;

    if (currentSrc && (currentSrc.startsWith('https') || currentSrc.startsWith('data:image'))) {
      fileOrBase = currentSrc;
    } else if (currentBase) {
      fileOrBase = currentBase;
    } else {
      fileOrBase = control.get('file').value;
    }

    if (fileOrBase instanceof File) {
      this._openCrop(fileOrBase, true);
    } else {
      this._openCrop(fileOrBase, false);
    }
  }

  private async _openCrop(image: File | string, asFile: boolean) {
    try {
      let file: File | null = null;
      let url: string | null = null;

      if (asFile) {
        file = image as File;
      } else if (typeof image === 'string' && /^https?:\/\//i.test(image)) {
        const response = await fetch(image, {mode: 'cors', cache: 'no-cache'});
        const blob = await response.blob();
        const nameFromUrl = image.split('/').pop()?.split('?')[0] || 'image';
        file = new File([blob], nameFromUrl, {type: blob.type});
      } else {
        url = image as string;
      }

      if (file) {
        await this.getImageResolution(file);
        this.openPhotoCropDialog(file, true, this.formGroup, this.cropConf);
      } else {
        await this.getImageResolutionUrl(url!);
        this.openPhotoCropDialog(url!, false, this.formGroup, this.cropConf);
      }
    } catch (err) {
      console.error(err);
    }
  }

  openPhotoCropDialog(imageURLorFile: File | string, imageFile: boolean, control: FormGroup, cropConf: any) {
    const data: any = {
      [imageFile ? 'imageFile' : 'imageURL']: imageURLorFile,
      ratio: cropConf.ratio,
      imgMedia: this.imgMedia,
      cropperMinWidth: cropConf.cropperMinWidth,
      cropperMinHeight: cropConf.cropperMinHeight,
      cropperMaxWidth: cropConf.cropperMaxWidth,
      cropperMaxHeight: cropConf.cropperMaxHeight,
      ...(cropConf.freeMaxCrop !== undefined && {freeMaxCrop: cropConf.freeMaxCrop}),
    };

    this.photoCropRef = this.dialog.open(PhotoCropComponent, {
      width: '1220px',
      data,
    });

    this.photoCropRef.afterClosed().subscribe((res: CroppedImageData) => {
      if (res?.saved) {
        control.get('file').setValue(res.croppedImageFile);
        control.get('base').setValue(null);
        control.get('source').setValue(res.croppedImage);
        this.cropped.emit(res);
      }
    });
  }

  getImageResolution(file: File): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const img = new Image();
        img.onload = () => {
          this.imgMedia = {width: img.width, height: img.height};
          resolve(this.imgMedia);
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  getImageResolutionUrl(url: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        this.imgMedia = {
          width: img.naturalWidth,
          height: img.naturalHeight,
        };
        resolve(this.imgMedia);
      };
      img.onerror = reject;
      img.src = url;
    });
  }

  removeImage() {
    this.formGroup.get('file').setValue(null);
    this.formGroup.get('base').setValue(null);
    this.formGroup.get('source').setValue('');
    if (this.rawVideoUrl) {
      URL.revokeObjectURL(this.rawVideoUrl);
      this.rawVideoUrl = this.safeVideoUrl = null;
    }
    this.imageRemoved.emit();
  }

  get isVideoSource(): boolean {
    const src = this.formGroup.get('source')!.value || '';
    return src.startsWith('blob:') || this.videoExts.some(ext => src.endsWith(`.${ext}`));
  }
}
